/**
 * SendEmailModal — Modal para enviar documento DTE por correo electrónico.
 * Pre-llena destinatarios con emails del cliente. Permite editar To/CC y adjuntar XML.
 */
import { useEffect, useState } from 'react'
import { X, Mail, Plus, Trash2, Loader2, CheckCircle2, Paperclip, FileText, FileCode2 } from 'lucide-react'
import { useDocumentEmailSuggestions, useSendDocumentEmail } from '@/modules/sales/hooks/useSales'
import { inputCls } from '@/lib/formStyles'
import { Modal } from '@/components/ds/Modal'

interface SendEmailModalProps {
  open: boolean
  onClose: () => void
  documentId: string
  documentLabel: string  // e.g. "Factura Electrónica N° 1247"
}

const tagCls = 'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-surface-muted text-content'

export function SendEmailModal({ open, onClose, documentId, documentLabel }: SendEmailModalProps) {
  const { data: suggestions, isLoading: loadingSuggestions } = useDocumentEmailSuggestions(open ? documentId : null)
  const sendMutation = useSendDocumentEmail()

  const [toEmails, setToEmails] = useState<string[]>([])
  const [ccEmails, setCcEmails] = useState<string[]>([])
  const [newTo, setNewTo] = useState('')
  const [newCc, setNewCc] = useState('')
  const [includeXml, setIncludeXml] = useState(false)
  const [sent, setSent] = useState(false)

  // Pre-fill when suggestions load
  useEffect(() => {
    if (suggestions) {
      setToEmails(suggestions.suggested_to)
      setCcEmails(suggestions.suggested_cc)
      setSent(false)
    }
  }, [suggestions])

  // Reset on close
  useEffect(() => {
    if (!open) {
      setNewTo('')
      setNewCc('')
      setIncludeXml(false)
      setSent(false)
    }
  }, [open])

  const addEmail = (list: string[], setList: (v: string[]) => void, value: string, setClear: (v: string) => void) => {
    const email = value.trim().toLowerCase()
    if (email && email.includes('@') && !list.includes(email)) {
      setList([...list, email])
      setClear('')
    }
  }

  const removeEmail = (list: string[], setList: (v: string[]) => void, idx: number) => {
    setList(list.filter((_, i) => i !== idx))
  }

  const handleSend = () => {
    if (toEmails.length === 0) return
    sendMutation.mutate(
      { documentId, to: toEmails, cc: ccEmails, include_xml: includeXml },
      { onSuccess: () => setSent(true) },
    )
  }

  if (!open) return null

  return (
    <Modal open onClose={onClose} title={<span className="flex items-center gap-2"><Mail size={16} className="text-primary" />Enviar por correo</span>} size="lg">
      <div className="space-y-4">
        {/* Document info */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-glass-3 border border-divider">
          <FileText size={14} className="text-content-subtle" />
          <span className="text-xs text-content-muted font-medium">{documentLabel}</span>
        </div>

        {loadingSuggestions ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin text-primary" size={20} />
          </div>
        ) : sent ? (
          <div className="flex flex-col items-center py-8 gap-3">
            <CheckCircle2 size={40} className="text-success-solid" />
            <p className="text-sm font-medium text-content">Correo enviado</p>
            <p className="text-xs text-content-muted text-center">
              El documento se está enviando a {toEmails.join(', ')}
            </p>
            <button onClick={onClose} className="mt-2 px-4 py-2 text-xs font-medium text-content-muted bg-surface-muted rounded-lg hover:bg-surface-muted transition-colors">
              Cerrar
            </button>
          </div>
        ) : (
          <>
            {/* To */}
            <div>
              <label className="block text-xs font-medium text-content-muted mb-1">
                Para <span className="text-danger-solid">*</span>
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {toEmails.map((email, idx) => (
                  <span key={email} className={tagCls}>
                    {email}
                    <button type="button" onClick={() => removeEmail(toEmails, setToEmails, idx)} className="text-content-subtle hover:text-danger-solid">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input aria-label="Agregar destinatario"
                  type="email"
                  value={newTo}
                  onChange={e => setNewTo(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(toEmails, setToEmails, newTo, setNewTo) } }}
                  placeholder="Agregar destinatario..."
                  className={inputCls}
                />
                <button type="button" onClick={() => addEmail(toEmails, setToEmails, newTo, setNewTo)}
                  className="px-3 py-2 text-xs font-medium text-primary bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors border border-primary-200 flex-shrink-0"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>

            {/* CC */}
            <div>
              <label className="block text-xs font-medium text-content-muted mb-1">CC</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {ccEmails.map((email, idx) => (
                  <span key={email} className={tagCls}>
                    {email}
                    <button type="button" onClick={() => removeEmail(ccEmails, setCcEmails, idx)} className="text-content-subtle hover:text-danger-solid">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input aria-label="Agregar destinatario en copia"
                  type="email"
                  value={newCc}
                  onChange={e => setNewCc(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(ccEmails, setCcEmails, newCc, setNewCc) } }}
                  placeholder="Agregar copia..."
                  className={inputCls}
                />
                <button type="button" onClick={() => addEmail(ccEmails, setCcEmails, newCc, setNewCc)}
                  className="px-3 py-2 text-xs font-medium text-content-muted bg-surface-muted rounded-lg hover:bg-soft transition-colors border border-divider flex-shrink-0"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>

            {/* Attachments */}
            <div>
              <label className="block text-xs font-medium text-content-muted mb-2">Adjuntos</label>
              <div className="space-y-2">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-50/50 border border-primary-100">
                  <Paperclip size={13} className="text-primary" />
                  <span className="text-xs text-content flex-1">PDF del documento</span>
                  <span className="text-[10px] text-primary font-medium">Siempre incluido</span>
                </div>
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-divider hover:bg-soft transition-colors cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeXml}
                    onChange={e => setIncludeXml(e.target.checked)}
                    className="rounded border-divider-strong text-primary w-3.5 h-3.5"
                  />
                  <FileCode2 size={13} className="text-content-subtle" />
                  <span className="text-xs text-content flex-1">Incluir XML del DTE</span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 text-xs font-medium text-content-muted bg-surface border border-divider rounded-xl hover:bg-soft transition-colors">
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={toEmails.length === 0 || sendMutation.isPending}
                className="flex-1 py-2.5 text-xs font-semibold text-on-primary bg-primary rounded-xl hover:bg-primary-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {sendMutation.isPending ? (
                  <><Loader2 size={14} className="animate-spin" /> Enviando...</>
                ) : (
                  <><Mail size={14} /> Enviar correo</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
